import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Handle, Position, useReactFlow, useStore } from '@xyflow/react';
import { nanoid } from 'nanoid';
import { toast } from 'react-toastify';
import ApiService from '../../../api/ApiService';
import { useProject } from '../providers/ProjectProvider';
import { useNodeOperations } from '../providers/NodeOperationsProvider';
import { TaskPricePreview } from './TaskPricePreview';
import { useTaskQuote } from './useTaskQuote';
import './ModernVideoModelNode.css';

const apiService = new ApiService();
const MISSING_PROMPT_ERROR = '请先连接文本输入节点并填写视频提示词';

const normalizeImageUrl = image => {
  if (typeof image === 'string') return image;
  return image?.url || image?.image_url || null;
};

const normalizeVideoUrl = node => {
  if (!node) return null;
  return node.data?.videoUrl
    || node.data?.video_url
    || node.data?.outputParams?.video_url
    || node.data?.outputParams?.video?.url
    || null;
};

const normalizeAudioUrl = node => {
  if (!node) return null;
  return node.data?.audioUrl
    || node.data?.audio_url
    || node.data?.resultUrl
    || node.data?.outputUrl
    || node.data?.outputParams?.audio_url
    || node.data?.outputParams?.audio?.url
    || node.data?.outputParams?.data?.audio
    || null;
};

const AUDIO_REFERENCE_NODE_TYPES = [
  'audioReference',
  'audioTask',
  'audioTrim',
  'textToAudio',
  'hailuoMusic',
  'sunoMusic',
];

export const ModernVideoModelNode = memo(({ id, data, config }) => {
  const { updateNodeData, getNode, getEdges } = useReactFlow();
  const edges = useStore(state => state.edges);
  const nodes = useStore(state => state.nodes);
  const { readOnly } = useProject();
  const { addNodesWithEdges, duplicateNode, createNodeAndConnect } = useNodeOperations();

  const [duration, setDuration] = useState(data.duration || config.defaultDuration);
  const [aspectRatio, setAspectRatio] = useState(data.aspectRatio || config.defaultAspectRatio);
  const [resolution, setResolution] = useState(data.resolution || config.defaultResolution);
  const [generateAudio, setGenerateAudio] = useState(
    data.generateAudio === undefined ? Boolean(config.defaultGenerateAudio) : Boolean(data.generateAudio)
  );
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);

  const durationMin = config.durationOptions[0];
  const durationMax = config.durationOptions[config.durationOptions.length - 1];
  const durationProgress = durationMax === durationMin
    ? 0
    : ((duration - durationMin) / (durationMax - durationMin)) * 100;

  useEffect(() => {
    updateNodeData(id, {
      duration,
      aspectRatio,
      resolution,
      ...(config.supportsAudio ? { generateAudio } : {}),
    });
  }, [id, duration, aspectRatio, resolution, generateAudio, config, updateNodeData]);

  const connectedPrompt = useMemo(() => edges
    .filter(edge => edge.target === id && edge.targetHandle === 'prompt')
    .map(edge => nodes.find(node => node.id === edge.source))
    .filter(node => node?.type === 'promptInput' && node.data?.prompt?.trim())
    .map(node => node.data.prompt.trim())
    .join('\n'), [edges, nodes, id]);

  const connectedImages = useMemo(() => {
    const imageUrls = [];
    edges
      .filter(edge => edge.target === id && edge.targetHandle === 'image')
      .forEach(edge => {
        const sourceNode = nodes.find(node => node.id === edge.source);
        if (sourceNode?.type === 'imageReference' || sourceNode?.type === 'imageDisplay') {
          imageUrls.push(sourceNode.data?.imageUrl);
        } else if (sourceNode?.type === 'drawing') {
          const url = sourceNode.data?.imageUrl;
          if (url && !url.startsWith('data:') && !url.startsWith('blob:')) imageUrls.push(url);
        } else if (sourceNode?.type === 'imageTask' && sourceNode.data?.status === 'completed') {
          (sourceNode.data.generatedImages || []).forEach(image => imageUrls.push(normalizeImageUrl(image)));
        }
      });
    return [...new Set(imageUrls.filter(Boolean))];
  }, [edges, nodes, id]);

  const connectedVideos = useMemo(() => {
    if (!config.videoLimit) return [];
    const videoUrls = edges
      .filter(edge => edge.target === id && edge.targetHandle === 'video')
      .map(edge => nodes.find(node => node.id === edge.source))
      .filter(node => ['videoReference', 'videoTask'].includes(node?.type))
      .map(normalizeVideoUrl)
      .filter(url => /^https?:\/\//i.test(url || ''));
    return [...new Set(videoUrls)];
  }, [config.videoLimit, edges, nodes, id]);

  const connectedAudios = useMemo(() => {
    if (!config.audioLimit) return [];
    const audioUrls = edges
      .filter(edge => edge.target === id && edge.targetHandle === 'audio')
      .map(edge => nodes.find(node => node.id === edge.source))
      .filter(node => AUDIO_REFERENCE_NODE_TYPES.includes(node?.type))
      .map(normalizeAudioUrl)
      .filter(url => /^https?:\/\//i.test(url || ''));
    return [...new Set(audioUrls)];
  }, [config.audioLimit, edges, nodes, id]);

  const inputParams = useMemo(() => config.buildParams(
    connectedPrompt,
    connectedImages,
    { duration, aspectRatio, resolution, generateAudio },
    connectedVideos,
    connectedAudios,
  ), [
    aspectRatio,
    config,
    connectedImages,
    connectedPrompt,
    connectedVideos,
    connectedAudios,
    duration,
    generateAudio,
    resolution,
  ]);

  const taskQuote = useTaskQuote({
    enabled: Boolean(connectedPrompt) && !readOnly,
    apiVersion: 'v3',
    model: config.modelId,
    params: inputParams,
  });

  useEffect(() => {
    if (connectedPrompt && error === MISSING_PROMPT_ERROR) {
      setError(null);
    }
  }, [connectedPrompt, error]);

  const isValidConnection = useCallback(connection => {
    const sourceNode = getNode(connection.source);
    const inputEdges = getEdges().filter(edge => edge.target === id);
    const referenceEdgeCount = inputEdges.filter(
      edge => edge.targetHandle === 'image' || edge.targetHandle === 'video'
    ).length;
    if (connection.targetHandle === 'prompt') return sourceNode?.type === 'promptInput';
    if (connection.targetHandle === 'image') {
      if (!['imageReference', 'drawing', 'imageTask', 'imageDisplay'].includes(sourceNode?.type)) {
        return false;
      }
      const imageEdgeCount = getEdges().filter(
        edge => edge.target === id && edge.targetHandle === 'image'
      ).length;
      if (imageEdgeCount >= config.imageLimit) {
        toast.warning(`${config.title} 最多支持 ${config.imageLimit} 个参考图输入`);
        return false;
      }
      if (config.referenceLimit && referenceEdgeCount >= config.referenceLimit) {
        toast.warning(`${config.title} 参考图和参考视频合计最多 ${config.referenceLimit} 个`);
        return false;
      }
    }
    if (connection.targetHandle === 'video') {
      if (!config.videoLimit || !['videoReference', 'videoTask'].includes(sourceNode?.type)) {
        return false;
      }
      const videoEdgeCount = getEdges().filter(
        edge => edge.target === id && edge.targetHandle === 'video'
      ).length;
      if (videoEdgeCount >= config.videoLimit) {
        toast.warning(`${config.title} 最多支持 ${config.videoLimit} 个参考视频输入`);
        return false;
      }
      if (config.referenceLimit && referenceEdgeCount >= config.referenceLimit) {
        toast.warning(`${config.title} 参考图和参考视频合计最多 ${config.referenceLimit} 个`);
        return false;
      }
    }
    if (connection.targetHandle === 'audio') {
      if (!config.audioLimit || !AUDIO_REFERENCE_NODE_TYPES.includes(sourceNode?.type)) {
        return false;
      }
      const audioEdgeCount = getEdges().filter(
        edge => edge.target === id && edge.targetHandle === 'audio'
      ).length;
      if (audioEdgeCount >= config.audioLimit) {
        toast.warning(`${config.title} 最多支持 ${config.audioLimit} 个参考音频输入`);
        return false;
      }
    }
    return true;
  }, [config.audioLimit, config.imageLimit, config.referenceLimit, config.title, config.videoLimit, getEdges, getNode, id]);

  const handleCreateInput = useCallback((event, handleId, handleType) => {
    event.stopPropagation();
    if (readOnly) {
      toast.info('只读模式下无法创建节点');
      return;
    }
    createNodeAndConnect(id, config.type, handleId, handleType);
  }, [config.type, createNodeAndConnect, id, readOnly]);

  const handleGenerate = async () => {
    try {
      setError(null);
      setIsGenerating(true);
      if (!connectedPrompt) throw new Error(MISSING_PROMPT_ERROR);
      if (connectedImages.length > config.imageLimit) {
        throw new Error(`参考图不能超过 ${config.imageLimit} 张`);
      }
      if (connectedVideos.length > (config.videoLimit || 0)) {
        throw new Error(`参考视频不能超过 ${config.videoLimit} 个`);
      }
      if (config.referenceLimit && connectedImages.length + connectedVideos.length > config.referenceLimit) {
        throw new Error(`参考图和参考视频合计不能超过 ${config.referenceLimit} 个`);
      }
      if (connectedAudios.length > (config.audioLimit || 0)) {
        throw new Error(`参考音频不能超过 ${config.audioLimit} 个`);
      }
      if (connectedAudios.length && !connectedImages.length && !connectedVideos.length) {
        throw new Error('参考音频需要搭配参考图片或参考视频使用');
      }

      const confirmedQuote = await taskQuote.quoteNow();
      const response = await apiService.createV3Task(config.modelId, inputParams);
      const taskId = response?.data?.task_id;
      if (!taskId) throw new Error(response?.message || '创建视频任务失败');

      const currentNode = getNode(id);
      const taskNodeId = nanoid();
      await addNodesWithEdges([
        {
          id: taskNodeId,
          type: 'videoTask',
          position: {
            x: currentNode.position.x + 450,
            y: currentNode.position.y,
          },
          data: {
            taskId,
            taskType: config.taskType,
            apiTaskType: 'v3_api',
            modelId: config.modelId,
            status: 'pending',
            inputParams,
            chargedPoints: Number(response?.data?.price ?? confirmedQuote.points),
            balanceAfterCharge: response?.data?.balance,
            videoUrl: null,
          },
        },
      ], [
        {
          id: nanoid(),
          source: id,
          target: taskNodeId,
          type: 'default',
        },
      ]);

      const referenceSummary = [
        connectedImages.length ? `${connectedImages.length} 张参考图` : '',
        connectedVideos.length ? `${connectedVideos.length} 个参考视频` : '',
        connectedAudios.length ? `${connectedAudios.length} 个参考音频` : '',
      ].filter(Boolean).join('、');
      toast.success(`${config.title} 任务已创建${referenceSummary ? `，使用 ${referenceSummary}` : ''}`);
    } catch (generationError) {
      console.error(`${config.title} 视频生成失败:`, generationError);
      const message = generationError?.message || '生成失败，请重试';
      setError(message);
      toast.error(message);
    } finally {
      setIsGenerating(false);
    }
  };

  const quoteBlocked = Boolean(connectedPrompt) && taskQuote.status !== 'success';
  const disabled = isGenerating || readOnly;
  const submitDisabled = disabled || quoteBlocked;

  return (
    <div
      className="ModernVideoModelNode-container"
      style={{ '--video-accent': config.accent, '--video-soft': config.soft }}
    >
      <button
        type="button"
        className="ModernVideoModelNode-duplicate"
        onClick={event => {
          event.stopPropagation();
          duplicateNode(id);
        }}
        title="复制节点"
        aria-label={`复制 ${config.title} 节点`}
      >
        <i className="bi bi-copy" />
      </button>

      <Handle
        type="target"
        position={Position.Left}
        id="prompt"
        className="ModernVideoModelNode-handle"
        style={{ top: config.audioLimit ? '18%' : (config.videoLimit ? '22%' : '28%') }}
        isValidConnection={isValidConnection}
      >
        <button
          type="button"
          className="ModernVideoModelNode-handle-label"
          onClick={event => handleCreateInput(event, 'prompt', 'text')}
          title="创建文本输入节点"
        >
          文本 +
        </button>
      </Handle>

      <Handle
        type="target"
        position={Position.Left}
        id="image"
        className="ModernVideoModelNode-handle"
        style={{ top: config.audioLimit ? '38%' : (config.videoLimit ? '48%' : '67%') }}
        isValidConnection={isValidConnection}
      >
        <button
          type="button"
          className="ModernVideoModelNode-handle-label"
          onClick={event => handleCreateInput(event, 'image', 'image')}
          title={`继续创建图片参考节点，最多 ${config.imageLimit} 张`}
        >
          参考图 +
        </button>
      </Handle>

      {Boolean(config.videoLimit) && (
        <Handle
          type="target"
          position={Position.Left}
          id="video"
          className="ModernVideoModelNode-handle ModernVideoModelNode-video-handle"
          style={{ top: config.audioLimit ? '58%' : '70%' }}
          isValidConnection={isValidConnection}
        >
          <button
            type="button"
            className="ModernVideoModelNode-handle-label"
            onClick={event => handleCreateInput(event, 'video', 'video')}
            title={`继续创建视频参考节点，最多 ${config.videoLimit} 个`}
          >
            参考视频 +
          </button>
        </Handle>
      )}

      {Boolean(config.audioLimit) && (
        <Handle
          type="target"
          position={Position.Left}
          id="audio"
          className="ModernVideoModelNode-handle ModernVideoModelNode-audio-handle"
          style={{ top: '78%' }}
          isValidConnection={isValidConnection}
        >
          <button
            type="button"
            className="ModernVideoModelNode-handle-label"
            onClick={event => handleCreateInput(event, 'audio', 'audio')}
            title={`继续创建音频参考节点，最多 ${config.audioLimit} 个`}
          >
            参考音频 +
          </button>
        </Handle>
      )}

      <Handle
        type="source"
        position={Position.Right}
        className="ModernVideoModelNode-handle ModernVideoModelNode-output-handle"
      />

      <div className="ModernVideoModelNode-header">
        <div className="ModernVideoModelNode-brand">
          <span className="ModernVideoModelNode-icon">{config.icon}</span>
          <div>
            <div className="ModernVideoModelNode-title">{config.title}</div>
            <div className="ModernVideoModelNode-model-id">{config.modelLabel}</div>
          </div>
        </div>
        <div className="ModernVideoModelNode-reference-caps">
          <span className="ModernVideoModelNode-reference-cap">{connectedImages.length}/{config.imageLimit} 图</span>
          {Boolean(config.videoLimit) && (
            <span className="ModernVideoModelNode-reference-cap ModernVideoModelNode-reference-cap-video">
              {connectedVideos.length}/{config.videoLimit} 视频
            </span>
          )}
          {Boolean(config.referenceLimit) && (
            <span className="ModernVideoModelNode-reference-cap ModernVideoModelNode-reference-cap-total">
              {connectedImages.length + connectedVideos.length}/{config.referenceLimit} 总素材
            </span>
          )}
          {Boolean(config.audioLimit) && (
            <span className="ModernVideoModelNode-reference-cap ModernVideoModelNode-reference-cap-audio">
              {connectedAudios.length}/{config.audioLimit} 音频
            </span>
          )}
        </div>
      </div>

      <div className="ModernVideoModelNode-body">
        <div className="ModernVideoModelNode-status-row">
          <span className={connectedPrompt ? 'is-ready' : ''}>
            <i className={`bi ${connectedPrompt ? 'bi-check-circle-fill' : 'bi-circle'}`} />
            提示词
          </span>
          <span className={connectedImages.length ? 'is-ready' : ''}>
            <i className="bi bi-collection-play" />
            {connectedImages.length ? `${connectedImages.length} 张参考图` : '可添加多参考'}
          </span>
          {Boolean(config.videoLimit) && (
            <span className={connectedVideos.length ? 'is-ready' : ''}>
              <i className="bi bi-film" />
              {connectedVideos.length ? `${connectedVideos.length} 个参考视频` : '可添加视频参考'}
            </span>
          )}
          {Boolean(config.audioLimit) && (
            <span className={connectedAudios.length ? 'is-ready' : ''}>
              <i className="bi bi-music-note-beamed" />
              {connectedAudios.length ? `${connectedAudios.length} 个参考音频` : '可添加音频参考'}
            </span>
          )}
        </div>

        <div className="ModernVideoModelNode-reference-strip">
          {connectedImages.length ? connectedImages.slice(0, 8).map((url, index) => (
            <span key={`${url}-${index}`} title={`参考图 ${index + 1}`}>{index + 1}</span>
          )) : (
            <small>点击左侧“参考图 +”可连续添加，连接顺序即引用顺序</small>
          )}
          {connectedImages.length > 8 && <em>+{connectedImages.length - 8}</em>}
        </div>

        {Boolean(config.videoLimit) && (
          <div className="ModernVideoModelNode-reference-strip ModernVideoModelNode-video-strip">
            {connectedVideos.length ? connectedVideos.map((url, index) => (
              <span key={`${url}-${index}`} title={`参考视频 ${index + 1}`}>
                <i className="bi bi-play-fill" />{index + 1}
              </span>
            )) : (
              <small>点击左侧“参考视频 +”添加动作、节奏或运镜参考</small>
            )}
          </div>
        )}

        {Boolean(config.audioLimit) && (
          <div className="ModernVideoModelNode-reference-strip ModernVideoModelNode-audio-strip">
            {connectedAudios.length ? connectedAudios.map((url, index) => (
              <span key={`${url}-${index}`} title={`参考音频 ${index + 1}`}>
                <i className="bi bi-music-note" />{index + 1}
              </span>
            )) : (
              <small>点击左侧“参考音频 +”添加音乐、节奏或声音风格参考</small>
            )}
          </div>
        )}

        <label className="ModernVideoModelNode-field ModernVideoModelNode-duration-field">
          <span>视频时长</span>
          <div className="ModernVideoModelNode-duration-control">
            <div className="ModernVideoModelNode-duration-slider-row">
              <input
                type="range"
                className="ModernVideoModelNode-duration-slider nodrag nopan"
                min={durationMin}
                max={durationMax}
                step="1"
                value={duration}
                onChange={event => setDuration(Number(event.target.value))}
                onPointerDown={event => event.stopPropagation()}
                disabled={disabled}
                aria-label="视频时长"
                aria-valuetext={`${duration} 秒`}
                style={{ '--duration-progress': `${durationProgress}%` }}
              />
              <output className="ModernVideoModelNode-duration-value">{duration} 秒</output>
            </div>
            <div className="ModernVideoModelNode-duration-limits" aria-hidden="true">
              <span>{durationMin} 秒</span>
              <span>{durationMax} 秒</span>
            </div>
          </div>
        </label>

        <label className="ModernVideoModelNode-field">
          <span>画面比例</span>
          <select className="nodrag nopan" value={aspectRatio} onChange={event => setAspectRatio(event.target.value)} disabled={disabled}>
            {config.aspectRatios.map(value => <option key={value} value={value}>{value}</option>)}
          </select>
        </label>

        <label className="ModernVideoModelNode-field">
          <span>分辨率</span>
          <select className="nodrag nopan" value={resolution} onChange={event => setResolution(event.target.value)} disabled={disabled}>
            {config.resolutions.map(value => <option key={value} value={value}>{value}</option>)}
          </select>
        </label>

        {config.supportsAudio && (
          <label
            className="ModernVideoModelNode-toggle nodrag nopan"
            onPointerDown={event => event.stopPropagation()}
          >
            <input
              type="checkbox"
              checked={generateAudio}
              onChange={event => setGenerateAudio(event.target.checked)}
              disabled={disabled}
            />
            <span>生成原生音频</span>
          </label>
        )}

        <div className="ModernVideoModelNode-hint">
          <i className="bi bi-lightbulb" />
          <span>{config.referenceHint}</span>
        </div>

        <TaskPricePreview quote={taskQuote} />

        {error && <div className="ModernVideoModelNode-error">{error}</div>}

        <button
          type="button"
          className="ModernVideoModelNode-generate nodrag nopan"
          onClick={handleGenerate}
          onPointerDown={event => event.stopPropagation()}
          disabled={submitDisabled}
        >
          {isGenerating ? (
            <><span className="ModernVideoModelNode-spinner" />正在创建任务</>
          ) : (
            <><i className="bi bi-camera-reels" />创建视频任务</>
          )}
        </button>
      </div>
    </div>
  );
});
